import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Minus, Star, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '—');
const pct = (v, x) => (v == null ? (x?.non_repondu ? 'Non répondu' : '—') : `${Math.round(v)} %`);
const VAL = { A: 'bg-emerald-500 text-white', ECA: 'bg-amber-400 text-white', NA: 'bg-red-500 text-white' };

const Sparkline = ({ points: allPoints }) => {
  const points = allPoints.filter(p => p.pourcentage != null);
  const vals = points.map(p => p.pourcentage);
  if (vals.length < 2) return null;
  const w = 90, h = 28;
  const xs = vals.map((_, i) => (i / (vals.length - 1)) * (w - 6) + 3);
  const ys = vals.map(v => h - 3 - (v / 100) * (h - 6));
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden>
      <polyline fill="none" stroke="#6366f1" strokeWidth="2" points={xs.map((x, i) => `${x},${ys[i]}`).join(' ')} />
      {xs.map((x, i) => <circle key={i} cx={x} cy={ys[i]} r="2.5" fill={points[i].retenu ? '#f59e0b' : '#6366f1'} />)}
    </svg>
  );
};

// Chronologie des tentatives par test + parcours d'acquisition des compétences (fiche candidat)
export const ProgressionTests = ({ dossierId, candidatId, compact = false }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/tentatives`).then(r => setData(r.data.tests)).catch(() => setData([]));
  }, [dossierId, candidatId]);
  if (!data) return <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-indigo-500" /></div>;
  const repasses = data.filter(t => t.nb_tentatives > 1);
  if (repasses.length === 0) return <p className="text-xs text-slate-400 italic" data-testid="progression-vide">Aucun test repassé : chaque QCM / évaluation n'a été passé qu'une fois.</p>;
  return (
    <div className="space-y-3" data-testid="progression-tests">
      {repasses.map(t => {
        const Icon = t.progression > 0 ? TrendingUp : t.progression < 0 ? TrendingDown : Minus;
        const cls = t.progression > 0 ? 'text-emerald-700' : t.progression < 0 ? 'text-red-700' : 'text-slate-500';
        return (
          <div key={`${t.test_type}-${t.test_id}`} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`progression-test-${t.test_id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${t.test_type === 'qcm' ? 'bg-indigo-50 text-indigo-700' : 'bg-violet-50 text-violet-700'}`}>{t.test_type === 'qcm' ? 'QCM' : 'Éval'}</span>
              <span className="text-sm font-medium text-slate-800 flex-1 min-w-0 truncate">{t.titre}</span>
              <Sparkline points={t.tentatives} />
              <span className={`text-xs font-semibold flex items-center gap-1 ${cls}`} data-testid={`progression-delta-${t.test_id}`}><Icon className="w-3.5 h-3.5" /> {t.progression == null ? '—' : `${t.progression > 0 ? '+' : ''}${t.progression} pts`}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {t.tentatives.map((x, i) => (
                <React.Fragment key={x.id}>
                  {i > 0 && <span className="text-slate-300">→</span>}
                  <span className={`px-2 py-0.5 rounded-md border ${x.retenu ? 'border-amber-300 bg-amber-50 text-amber-900 font-semibold' : 'border-slate-200 bg-white text-slate-600'}`} title={x.retenu ? 'Meilleure tentative — retenue dans les bilans' : ''} data-testid={`tentative-${x.id}`}>
                    {x.retenu && <Star className="w-3 h-3 inline mr-0.5 fill-amber-500 text-amber-500" />}Tentative {x.n} : {pct(x.pourcentage, x)}{x.score != null && x.score_max ? ` (${x.score}/${x.score_max})` : ''} <span className="text-slate-400 font-normal">· {fmt(x.date)}</span>{x.delta != null && <span className={`ml-1 ${x.delta > 0 ? 'text-emerald-700' : x.delta < 0 ? 'text-red-700' : 'text-slate-400'}`}>{x.delta > 0 ? '▲' : x.delta < 0 ? '▼' : '='} {Math.abs(x.delta)}</span>}
                  </span>
                </React.Fragment>
              ))}
            </div>
            {!compact && t.competences.length > 0 && (
              <div className="pt-1 border-t border-slate-100">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Acquisition des compétences {t.competences_acquises_apres_repassage > 0 && <span className="text-emerald-700 font-semibold normal-case">· {t.competences_acquises_apres_repassage} acquise(s) grâce au repassage</span>}</p>
                <ul className="space-y-0.5">
                  {t.competences.map(c => (
                    <li key={c.competence_id} className="flex items-center gap-2 text-xs" data-testid={`competence-parcours-${t.test_id}-${c.competence_id}`}>
                      <span className="flex-1 min-w-0 truncate text-slate-700">{c.titre}</span>
                      <span className="flex items-center gap-1">{c.parcours.map((p, i) => <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${VAL[p.valeur] || 'bg-slate-200 text-slate-600'}`} title={`Tentative ${p.n} · ${fmt(p.date)}`}>{p.valeur || '—'}</span>)}</span>
                      <span className="w-28 text-right text-[10px] text-slate-500">{c.acquise_le ? `acquise le ${fmt(c.acquise_le)}` : 'non acquise'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Vue groupe (suivi pédagogique) : effet des repassages par test
export const ProgressionGroupe = ({ dossierId, refreshKey }) => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/dossiers/${dossierId}/progression-tests`).then(r => setData(r.data.tests)).catch(() => setData([])); }, [dossierId, refreshKey]);
  if (!data || data.length === 0) return null;
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid="progression-groupe">
      <div className="px-3 py-2 border-b bg-slate-50 rounded-t-lg flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" /><h3 className="text-sm font-semibold text-slate-800">Effet des repassages — {data.length} test(s)</h3></div>
      <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 border-b"><span>Test</span><span>Candidats repassés</span><span>Moy. 1ʳᵉ → meilleure</span><span>Progressé / régressé</span><span>Compétences acquises</span></div>
      {data.map(t => (
        <div key={`${t.test_type}-${t.test_id}`} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 text-xs items-center border-b last:border-b-0" data-testid={`progression-groupe-${t.test_id}`}>
          <span className="font-medium text-slate-800 truncate">{t.titre}</span>
          <span>{t.nb_candidats_repasses}</span>
          <span className="font-semibold">{pct(t.moyenne_premiere)} → <span className="text-emerald-700">{pct(t.moyenne_meilleure)}</span></span>
          <span><span className="text-emerald-700 font-semibold">{t.nb_progresse} ▲</span> · <span className="text-red-700">{t.nb_regresse} ▼</span></span>
          <span className="text-emerald-700">{t.competences_acquises_apres_repassage}</span>
        </div>
      ))}
    </section>
  );
};
